import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, tap } from 'rxjs';
import { Logger, ConfirmDialogComponent } from 'src/app/@shared';
import { OfferTag, OfferTagService } from '../..';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import * as dayjs from 'dayjs';
import { CUSTOM_DATE_ADAPTER_OPTIONS, CustomDateAdapter } from 'src/app/@shared/directives/custom-date-adapter';
import { DateAdapter } from '@angular/material/core';

const log = new Logger('OfferTagComponent');

@Component({
  selector: 'app-offer-tag',
  templateUrl: './offer-tag.component.html',
  styleUrls: ['./offer-tag.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: CUSTOM_DATE_ADAPTER_OPTIONS, useValue: { useDefaultDate: false } }
  ]
})
export class OfferTagComponent<T extends OfferTag> implements OnInit {

  offerTag$: Observable<T> = of({} as T);
  formGroup = new FormGroup({
    ClientKey: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
    Id: new FormControl<string>({ value: '0', disabled: true }, { nonNullable: true }),
    OfferTagName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
    Title: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100)]),
    Description: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(200)]),
    Disclaimer: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(200)]),
    StartDate: new FormControl<Date | string | null>(null, []),
    EndDate: new FormControl<Date | string | null>(null, []),
    IsHidden: new FormControl<boolean>(false, { nonNullable: true })
  });
  offerTagId: string = '0';

  constructor(private router: Router,
    private route: ActivatedRoute,
    private offerTagService: OfferTagService<T>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    log.debug('init');
    // get the offerTag id from the parent route
    this.offerTagId = this.route.snapshot.params['offertagId'];

    // if we are editing a record, get the record data
    if (this.offerTagId && this.offerTagId !== '0') {
      this.offerTag$ = this.offerTagService.getOfferTag(this.offerTagId).pipe(
        tap((offerTag: any) => {
          offerTag.StartDate = this.normalizeDate(offerTag.StartDate);
          offerTag.EndDate = this.normalizeDate(offerTag.EndDate);

          this.formGroup.patchValue(offerTag);
          this.formGroup.markAllAsTouched();
        })
      );
    }
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }

  validateDate(): boolean {
    let StartDate = this.formGroup.controls.StartDate;
    let EndDate = this.formGroup.controls.EndDate;
    if (EndDate && EndDate.value && StartDate && StartDate.value) {
      if (EndDate.value <= StartDate.value) {
        this.matSnackBar.open(
          'End Date must be greater than Start Date',
          'Error',
          { verticalPosition: 'top', panelClass: ['snackbar-error'] }
        );
        return false;
      }
    }
    return true;
  }

  save() {
    this.trimControlValues(this.formGroup);
    if (this.formGroup.valid) {
      if (!this.validateDate()) return;
      const raw: any = this.formGroup.getRawValue() as T;
      const payload: any = {
        ...raw
      };

      if (raw.StartDate)
        payload.StartDate = dayjs(raw.StartDate).format('MM/DD/YYYY');
      else
        delete payload.StartDate;

      if (raw.EndDate)
        payload.EndDate = dayjs(raw.EndDate).format('MM/DD/YYYY');
      else
        delete payload.EndDate;

      this.offerTagService.saveOfferTag(payload).subscribe({
        next: (response) => {
          this.matSnackBar.open(
            `${this.formGroup.controls.OfferTagName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.offerTagService.reload();
          this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving offerTag', error);
            this.matSnackBar.open('500 Error saving offerTag', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                if (this.formGroup?.get(validation.PropertyName)) {
                  const control = this.formGroup?.get(validation.PropertyName);
                  if (control) {
                    control.markAsTouched();
                    control.setErrors({ invalid: validation.ErrorMessage });
                    this.matSnackBar.open(validation.ErrorMessage, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                  }
                } else {
                  ///TODO: if we have cross field validation then show the validation error at the top of the screen
                  // if we have cross field validation then show the validation error at the top of the screen
                  // push general error messages to array this is displayed in a toast or dialog
                }
              });
            } else {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }


          }
        }
      });
    }
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  deleteRecord() {
    const record = this.formGroup.getRawValue();
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.OfferTagName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.offerTagService.deleteOfferTag(record.Id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.OfferTagName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.offerTagService.reload();
              this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting event', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

  normalizeDate(value: any): Date | null {
    if (!value || value === '01/01/0001')
      return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

}
